/**
 * ChatContext - Generic context system for AI chat
 *
 * Allows any part of the app to set context that will be sent to the AI.
 * Context is structured and extensible.
 *
 * Usage:
 *   // Set focus context (what the user wants to talk about)
 *   ChatContext.setFocus({ type: 'invoice', id: 'abc-123', data: { amount: 100 } })
 *
 *   // Clear focus
 *   ChatContext.clearFocus()
 *
 *   // Get full context for sending to server
 *   ChatContext.getContext()
 */

class ChatContextManager {
  constructor() {
    this.focus = null
    this.listeners = new Set()
  }

  /**
   * Set the focus context - what the user wants to talk about
   * @param {Object} focus - The focus object
   * @param {string} focus.type - Type of entity (e.g., 'invoice', 'image', 'task', 'document')
   * @param {string} focus.id - Entity ID
   * @param {string} [focus.label] - Human-readable label for display
   * @param {Object} [focus.data] - Additional data relevant to the entity
   */
  setFocus(focus) {
    this.focus = focus
    this._notifyListeners()
  }

  /**
   * Clear the focus context
   */
  clearFocus() {
    this.focus = null
    this._notifyListeners()
  }

  /**
   * Get the current focus
   * @returns {Object|null}
   */
  getFocus() {
    return this.focus
  }

  /**
   * Build page context from current URL
   * @returns {Object}
   */
  getPageContext() {
    const location = window.location
    const pathSegments = location.pathname.split('/').filter(Boolean)

    // Extract resource IDs from URL path
    const resources = {}
    const resourceTypes = ['sites', 'projects', 'organizations', 'users', 'tasks', 'events']

    for (let i = 0; i < pathSegments.length - 1; i++) {
      const segment = pathSegments[i]
      const nextSegment = pathSegments[i + 1]
      if (resourceTypes.includes(segment)) {
        const paramName = segment.replace(/s$/, '_id')
        resources[paramName] = nextSegment
      }
    }

    return {
      href: location.href,
      pathname: location.pathname,
      search: location.search,
      resources,
    }
  }

  /**
   * Get full context object for sending to server
   * @returns {Object}
   */
  getContext() {
    return {
      page: this.getPageContext(),
      focus: this.focus,
    }
  }

  /**
   * Subscribe to context changes
   * @param {Function} callback
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  _notifyListeners() {
    const context = this.getContext()
    this.listeners.forEach(callback => callback(context))
  }

  /**
   * Observe dialog containers for chat context changes.
   * When a dialog opens with data-chat-context-* attributes, sets focus.
   * When dialog closes (container empties), clears focus.
   */
  observeDialogContainers() {
    if (this._dialogObserversInitialized) return
    this._dialogObserversInitialized = true

    const containerIds = ['main_modal', 'main_offsetcanvas']

    containerIds.forEach(id => {
      const container = document.getElementById(id)
      if (!container) return

      const observer = new MutationObserver(() => {
        const contextEl = container.querySelector('[data-chat-context-type]')
        if (contextEl) {
          this.setFocus({
            type: contextEl.dataset.chatContextType,
            id: contextEl.dataset.chatContextId,
            label: contextEl.dataset.chatContextLabel,
          })
        } else if (container.children.length === 0) {
          this.clearFocus()
        }
      })

      observer.observe(container, { childList: true, subtree: true })
    })
  }
}

// Singleton instance
const ChatContext = new ChatContextManager()

// Expose globally for use outside of ES modules (e.g., inline scripts, console)
if (typeof window !== 'undefined') {
  window.ChatContext = ChatContext
}

export default ChatContext
